/**
 * Where a file came from, as the tenant filed it.
 *
 * This is NOT attribution and must never be drawn as though it were. What
 * produced a file comes from its own bytes; what it came from comes from the
 * folder the tenant put it in. Both are true at once:
 *
 *     imports/github-repo/CapWayInc/cpwy-cli/package.json
 *       produced by  ->  npm      (its bytes, the existing tool badge)
 *       came from    ->  GitHub   (the tenant's filing, this)
 *
 * The backend serves them from separate endpoints so the mistake is hard to
 * make, and nothing here reads or writes a tool badge.
 *
 * Why it exists: 84.6% of tenant objects come back with no tool, and that is
 * mostly not fixable - a `.ts` file inside a repository export carries no
 * fingerprint of the system it came from. On capway that is 20,341 files that
 * were tens of thousands of "unknown" rows and are now one node saying GitHub.
 */
import type { components } from '@/api/generated/openapi';

export type SourceCollection = components['schemas']['SourceCollectionsEnvelope']['data']['items'][number];

export interface CollectionNode {
  readonly item: SourceCollection;
  readonly children: CollectionNode[];
}

/**
 * The tenant's filing, as a tree.
 *
 * Built iteratively. The live data runs eighteen levels deep on capway and the
 * contract says depth is unbounded, so a recursive build is a stack waiting for
 * a deeper tenant.
 *
 * Order is preserved exactly as the API returned it - by `pathPrefix` - because
 * that IS the tenant's own filing order and re-sorting it would substitute ours.
 */
export function buildCollectionTree(items: readonly SourceCollection[]): CollectionNode[] {
  const nodes = new Map<string, CollectionNode>();
  for (const item of items) nodes.set(item.id, { item, children: [] });

  const roots: CollectionNode[] = [];
  for (const item of items) {
    const node = nodes.get(item.id)!;
    const parent = item.parentId === null ? undefined : nodes.get(item.parentId);
    // A parent id naming something absent from this response would otherwise
    // drop the whole subtree under it. It surfaces as a root instead: showing
    // a folder at the wrong level is recoverable, losing 20,000 files is not.
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/**
 * What to call this collection.
 *
 * `toolId: null` is an ANSWER, not a gap. A folder called `Corpus` names no
 * product and the API refuses to guess, deliberately, because a wrong system
 * is worse than none. So a nameless collection is its own folder name, never
 * "Unknown system" and never an error state. On capway 4,316 of 4,319
 * collections are in exactly that position, so this is the ordinary case
 * rather than the edge one.
 */
export function collectionLabel(item: SourceCollection): string {
  return item.toolLabel ?? item.segment;
}

/** Whether this collection names a system the tenant exported from. */
export function namesASystem(item: SourceCollection): boolean {
  return Boolean(item.toolId && item.toolLabel);
}

/**
 * Files beneath a collection.
 *
 * `memberCount` is EVERYTHING under it, not its direct children, so summing a
 * node's children double-counts every file in the subtree. This exists so no
 * caller is tempted to add them up.
 */
export function memberCount(node: CollectionNode): number {
  return node.item.memberCount;
}

/**
 * The roots, largest first, for a panel that shows a few and counts the rest.
 *
 * Filing order is right inside a folder, where it is the tenant's own
 * arrangement. At the top level what an operator wants first is the twenty
 * thousand files, not whichever prefix sorts earliest.
 */
export function rootsByWeight(roots: readonly CollectionNode[]): CollectionNode[] {
  return [...roots].sort((a, b) => b.item.memberCount - a.item.memberCount);
}

/**
 * What this tree covers, said without overclaiming.
 *
 * An object filed at the bucket root belongs to no collection and appears in
 * none of this, so the total here is not the bucket's object count and must
 * never be presented as one.
 */
export function coverage(roots: readonly CollectionNode[]): { filed: number; collections: number } {
  let collections = 0;
  const stack = [...roots];
  while (stack.length > 0) {
    const node = stack.pop()!;
    collections += 1;
    for (const child of node.children) stack.push(child);
  }
  // Roots partition the filing, so the top level sums without double-counting
  // where the subtree does not.
  return { filed: roots.reduce((n, r) => n + r.item.memberCount, 0), collections };
}
